import {
    ChangeDetectionStrategy,
    Component,
    effect,
    Injector,
    inject,
    input,
    numberAttribute,
    output,
    untracked,
} from '@angular/core';
import { areSliderStatesEqual, SLIDER_INITIAL_STATE } from './cost-to-build-client-ui-slider.state';
import { SliderStore } from './cost-to-build-client-ui-slider.store';
import type { SliderState } from './cost-to-build-client-ui-slider.types';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [],
    providers: [SliderStore],
    selector: 'ctb-slider',
    styleUrl: './cost-to-build-client-ui-slider.scss',
    templateUrl: './cost-to-build-client-ui-slider.html',
})
export class CtbSliderComponent {
    readonly #injector = inject(Injector);
    readonly #store = inject(SliderStore);

    readonly ariaLabel = input<string>(SLIDER_INITIAL_STATE.ariaLabel);
    readonly id = input<string>(SLIDER_INITIAL_STATE.id);
    readonly label = input<string>(SLIDER_INITIAL_STATE.label);
    readonly max = input(SLIDER_INITIAL_STATE.max, { transform: numberAttribute });
    readonly min = input(SLIDER_INITIAL_STATE.min, { transform: numberAttribute });
    readonly step = input(SLIDER_INITIAL_STATE.step, { transform: numberAttribute });
    readonly value = input(SLIDER_INITIAL_STATE.value, { transform: numberAttribute });

    readonly valueChange = output<number>();
    readonly stateChange = output<SliderState>();

    readonly formattedMax = this.#store.formattedMax;
    readonly formattedMin = this.#store.formattedMin;
    readonly formattedValue = this.#store.formattedValue;
    readonly progressPercent = this.#store.progressPercent;
    readonly resolvedAriaLabel = this.#store.resolvedAriaLabel;
    readonly state = this.#store.state;

    #lastEmittedState: SliderState | null = null;

    readonly syncStoreFromInputs = effect(() => this.#syncStoreFromInputs(), { injector: this.#injector });

    readonly emitStateChanges = effect(
        () => {
            const nextState = this.state();
            const previousState = this.#lastEmittedState;

            if (previousState !== null && areSliderStatesEqual(previousState, nextState)) {
                return;
            }

            this.#lastEmittedState = nextState;
            this.stateChange.emit(nextState);
        },
        { injector: this.#injector }
    );

    onTooltipVisibilityChange(tooltipVisible: boolean) {
        this.#store.setTooltipVisible(tooltipVisible);
    }

    onValueInput(event: Event) {
        if (!(event.target instanceof HTMLInputElement)) {
            return;
        }

        const nextValue = Number(event.target.value);

        this.#store.setValue(nextValue);
        this.valueChange.emit(this.state().value);
    }

    #syncStoreFromInputs() {
        const patch = {
            ariaLabel: this.ariaLabel(),
            id: this.id(),
            label: this.label(),
            max: this.max(),
            min: this.min(),
            step: this.step(),
            value: this.value(),
        };

        untracked(() => this.#store.setState(patch));
    }
}
